import { useState, useCallback, useEffect, useRef } from 'react';
import { message, Modal } from 'antd';
import dayjs from 'dayjs';
import { apiRequests } from 'src/shared/api/api';

const BOX_LENGTH = 23;
const BOX_WIDTH  = 19;
const BOX_HEIGHT = 10;

const computeBoxWeight = (pkgItems = []) =>
  pkgItems.reduce((sum, item) => sum + (item?.weight || 0) * (item?.amount || 1), 0) || 1000;

const useCdekOrder = ({
  docId,
  token,
  form,
  items,
  warehouse,
  recipient,
  onCdekOrderCreated,
  onCdekOrderDeleted,
}) => {
  
  const [orderType,      setOrderType]      = useState('im');
  const [recipientLegal, setRecipientLegal] = useState(false);

  
  const [pickupMode,   setPickupMode]   = useState('door');
  const [deliveryMode, setDeliveryMode] = useState('door');

  
  const [allTariffs,         setAllTariffs]         = useState([]);
  const [availableTariffs,   setAvailableTariffs]   = useState([]);
  const [selectedTariffCode, setSelectedTariffCode] = useState(null);
  const [calculatedPrice,    setCalculatedPrice]    = useState(null);
  const [detailedPrice,      setDetailedPrice]      = useState(null);

  
  const [loadingTariffs,         setLoadingTariffs]         = useState(false);
  const [loadingCreate,          setLoadingCreate]          = useState(false);
  const [loadingDetailed,        setLoadingDetailed]        = useState(false);
  const [loadingTemplate,        setLoadingTemplate]        = useState(false);
  const [loadingOrderInfo,       setLoadingOrderInfo]       = useState(false);
  const [loadingWaybillInfo,     setLoadingWaybillInfo]     = useState(false);
  const [loadingWaybillDownload, setLoadingWaybillDownload] = useState(false);
  const [loadingBarcodeInfo,     setLoadingBarcodeInfo]     = useState(false);
  const [loadingBarcodeDownload, setLoadingBarcodeDownload] = useState(false);

  
  const [cdekOrder,   setCdekOrder]   = useState(null);
  const [waybillUuid, setWaybillUuid] = useState(null);
  const [barcodeUuid, setBarcodeUuid] = useState(null);
  const [waybillInfo, setWaybillInfo] = useState(null);
  const [barcodeInfo, setBarcodeInfo] = useState(null);

  // ── Профиль личного кабинета СДЭК ────────────────────────────────────────────
  const [lkProfile, setLkProfile] = useState(null);

  
  const [senderCityOptions,    setSenderCityOptions]    = useState([]);
  const [recipientCityOptions, setRecipientCityOptions] = useState([]);
  const [loadingSenderCity,    setLoadingSenderCity]    = useState(false);
  const [loadingRecipientCity, setLoadingRecipientCity] = useState(false);
  const [selectedSenderCity,    setSelectedSenderCity]    = useState(null);
  const [selectedRecipientCity, setSelectedRecipientCity] = useState(null);
  const [senderSearch,    setSenderSearch]    = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');

  
  const [pickupAddressOptions,   setPickupAddressOptions]   = useState([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState([]);
  const [loadingPickupAddress,   setLoadingPickupAddress]   = useState(false);
  const [loadingDeliveryAddress, setLoadingDeliveryAddress] = useState(false);
  const [pickupAddressSearch,   setPickupAddressSearch]   = useState('');
  const [deliveryAddressSearch, setDeliveryAddressSearch] = useState('');

  
  const [pickupPvzOptions,   setPickupPvzOptions]   = useState([]);
  const [deliveryPvzOptions, setDeliveryPvzOptions] = useState([]);
  const [loadingPickupPvz,   setLoadingPickupPvz]   = useState(false);
  const [loadingDeliveryPvz, setLoadingDeliveryPvz] = useState(false);
  const [selectedPickupPvz,    setSelectedPickupPvz]    = useState(null);
  const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(null);

  
  const parseTariffName = (name) => {
    const match = name?.match(/(дверь|склад|постамат)[-](дверь|склад|постамат)/);
    if (!match) return { senderType: null, receiverType: null };
    const [s, r] = match[0].split('-');
    const map = (w) => ({ дверь: 'door', склад: 'pvz', постамат: 'postamat' }[w] ?? null);
    return { senderType: map(s), receiverType: map(r) };
  };

  const filterTariffsByMode = useCallback((tariffs, pMode, dMode) => {
    const filtered = tariffs.filter((t) => {
      const { senderType, receiverType } = parseTariffName(t.tariff_name);
      if (!senderType || !receiverType) return false;
      const sMatch = (pMode === 'door' && senderType === 'door') || (pMode === 'pvz' && senderType === 'pvz');
      const rMatch =
        (dMode === 'door'     && receiverType === 'door') ||
        (dMode === 'pvz'      && receiverType === 'pvz')  ||
        (dMode === 'postamat' && receiverType === 'postamat');
      return sMatch && rMatch;
    });
    setAvailableTariffs(filtered);
    if (filtered.length > 0) {
      setSelectedTariffCode(filtered[0].tariff_code);
      setCalculatedPrice(filtered[0]);
    } else {
      setSelectedTariffCode(null);
      setCalculatedPrice(null);
    }
  }, []);

  
  const fetchSenderCities = useCallback(async (search) => {
    if (!search) return;
    setLoadingSenderCity(true);
    try {
      const res = await apiRequests.integrationCdek.suggestCities({ name: search }, token);
      setSenderCityOptions((res.data || []).map((c) => ({ label: c.full_name, value: c.full_name, data: c })));
    } catch (e) { console.error(e); } finally { setLoadingSenderCity(false); }
  }, [token]);

  const fetchRecipientCities = useCallback(async (search) => {
    if (!search) return;
    setLoadingRecipientCity(true);
    try {
      const res = await apiRequests.integrationCdek.suggestCities({ name: search }, token);
      setRecipientCityOptions((res.data || []).map((c) => ({ label: c.full_name, value: c.full_name, data: c })));
    } catch (e) { console.error(e); } finally { setLoadingRecipientCity(false); }
  }, [token]);

  useEffect(() => { const t = setTimeout(() => senderSearch    && fetchSenderCities(senderSearch), 300);    return () => clearTimeout(t); }, [senderSearch,    fetchSenderCities]);
  useEffect(() => { const t = setTimeout(() => recipientSearch && fetchRecipientCities(recipientSearch), 300); return () => clearTimeout(t); }, [recipientSearch, fetchRecipientCities]);

  
  const fetchPickupAddress = useCallback(async (value) => {
    if (!value || !selectedSenderCity?.uuid) return;
    setLoadingPickupAddress(true);
    try {
      const res = await apiRequests.integrationCdek.addressSuggestions({ cityUuid: selectedSenderCity.uuid, value }, token);
      setPickupAddressOptions((res.data?.items || []).map((a) => ({ label: a.suggestion, value: a.suggestion, data: a })));
    } catch (e) { console.error(e); } finally { setLoadingPickupAddress(false); }
  }, [token, selectedSenderCity]);

  const fetchDeliveryAddress = useCallback(async (value) => {
    if (!value || !selectedRecipientCity?.uuid) return;
    setLoadingDeliveryAddress(true);
    try {
      const res = await apiRequests.integrationCdek.addressSuggestions({ cityUuid: selectedRecipientCity.uuid, value }, token);
      setDeliveryAddressOptions((res.data?.items || []).map((a) => ({ label: a.suggestion, value: a.suggestion, data: a })));
    } catch (e) { console.error(e); } finally { setLoadingDeliveryAddress(false); }
  }, [token, selectedRecipientCity]);

  useEffect(() => { const t = setTimeout(() => pickupAddressSearch   && fetchPickupAddress(pickupAddressSearch), 300);   return () => clearTimeout(t); }, [pickupAddressSearch,   fetchPickupAddress]);
  useEffect(() => { const t = setTimeout(() => deliveryAddressSearch && fetchDeliveryAddress(deliveryAddressSearch), 300); return () => clearTimeout(t); }, [deliveryAddressSearch, fetchDeliveryAddress]);

  
  const loadPickupPvz = useCallback(async (cityCode) => {
    if (!cityCode) return;
    setLoadingPickupPvz(true);
    try {
      const res = await apiRequests.integrationCdek.getDeliveryPoints({ city_code: cityCode }, token);
      setPickupPvzOptions((res.data || []).map((p) => ({ value: p.code, label: p.location?.address_full || `${p.location?.address} (${p.code})`, data: p })));
    } catch (e) { console.error(e); } finally { setLoadingPickupPvz(false); }
  }, [token]);

  const loadDeliveryPvz = useCallback(async (cityCode) => {
    if (!cityCode) return;
    setLoadingDeliveryPvz(true);
    try {
      const res = await apiRequests.integrationCdek.getDeliveryPoints({ city_code: cityCode }, token);
      setDeliveryPvzOptions((res.data || []).map((p) => ({ value: p.code, label: p.location?.address_full || `${p.location?.address} (${p.code})`, data: p })));
    } catch (e) { console.error(e); } finally { setLoadingDeliveryPvz(false); }
  }, [token]);

  useEffect(() => {
    if (pickupMode === 'pvz' && selectedSenderCity?.code) loadPickupPvz(selectedSenderCity.code);
    else setPickupPvzOptions([]);
  }, [pickupMode, selectedSenderCity, loadPickupPvz]);

  useEffect(() => {
    if ((deliveryMode === 'pvz' || deliveryMode === 'postamat') && selectedRecipientCity?.code) loadDeliveryPvz(selectedRecipientCity.code);
    else setDeliveryPvzOptions([]);
  }, [deliveryMode, selectedRecipientCity, loadDeliveryPvz]);

  
  useEffect(() => {
    if (allTariffs.length > 0) filterTariffsByMode(allTariffs, pickupMode, deliveryMode);
  }, [pickupMode, deliveryMode, allTariffs, filterTariffsByMode]);

  
  const updateFormWithItemsAndWarehouse = useCallback(() => {
    const patch = {
      sender_company: warehouse?.name || '',
      sender_phone:   warehouse?.phone || '+7',
    };
    if (recipient) {
      const fullName =
        recipient.name ||
        `${recipient.first_name || ''} ${recipient.last_name || ''}`.trim() ||
        recipient.full_name || '';
      patch.recipient_fio    = fullName;
      patch.recipient_phone  = recipient.phone || recipient.mobile || '';
      patch.recipient_email  = recipient.email || '';
    }
    patch.packages = items.length > 0
      ? [{
          is_dangerous: false,
          items: items.map((item, idx) => ({
            name:     item.name || '',
            ware_key: item.id?.toString() || `item-${idx}`,
            weight:   item.weight || 1000,
            amount:   item.count || 1,
            cost:     item.amount || 0,
          })),
        }]
      : [];
    form.setFieldsValue(patch);
  }, [items, warehouse, form, recipient]);

  
  const syncFromRegularDelivery = useCallback((deliveryData) => {
    if (!deliveryData) return;
    const recipientName =
      deliveryData.recipient_fullname ||
      (deliveryData.recipient ? `${deliveryData.recipient.name || ''} ${deliveryData.recipient.surname || ''}`.trim() : '');
    const recipientPhone = deliveryData.recipient_phone || deliveryData.recipient?.phone || '';
    const patch = {};
    if (recipientName)          patch.recipient_fio    = recipientName;
    if (recipientPhone)         patch.recipient_phone  = recipientPhone;
    if (deliveryData.address)   patch.delivery_address = deliveryData.address;
    if (Object.keys(patch).length) form.setFieldsValue(patch);
  }, [form]);

  // ── Загрузить профиль ЛК (с кешированием в state) ────────────────────────────
  const fetchLkProfile = useCallback(async () => {
    if (!token) return null;
    // Если уже закешировано — возвращаем сразу
    if (lkProfile) return lkProfile;
    try {
      const res = await apiRequests.integrationCdek.lkSession(token);
      if (res.data?.has_session && res.data?.profile) {
        setLkProfile(res.data.profile);
        return res.data.profile;
      }
    } catch (e) {
      console.error('lkSession error', e);
    }
    return null;
  }, [token, lkProfile]);

  // ── Применить данные ЛК к пустым полям формы ─────────────────────────────────
  const applyLkProfileFallback = useCallback((profile) => {
    if (!profile) return;
    const current = form.getFieldsValue();
    const updates = {};

    if (!current.sender_company && profile.displayName)
      updates.sender_company = profile.displayName;

    if (!current.sender_fio && profile.contactName)
      updates.sender_fio = profile.contactName;

    if (!current.sender_phone && profile.phones?.[0]?.number)
      updates.sender_phone = profile.phones[0].number;

    if (!current.sender_inn && profile.inn)
      updates.sender_inn = profile.inn;

    // Город отправителя — только если не задан ни city_data, ни текстовое поле
    if (!current.sender_city_data && !current.sender_city && profile.addressLegal?.city) {
      const cityData = {
        code:      profile.addressLegal.city.code,
        uuid:      profile.addressLegal.city.uuid,
        full_name: profile.addressLegal.city.fullName,
        ...profile.addressLegal.city,
      };
      updates.sender_city_data = cityData;
      updates.sender_city      = cityData.full_name;
      setSelectedSenderCity(cityData);
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [form]);

  
  const loadTemplate = useCallback(async () => {
    if (!token) return;
    setLoadingTemplate(true);
    try {
      // Шаг 1: параллельно загружаем шаблон и профиль ЛК
      const [templateRes, profile] = await Promise.all([
        apiRequests.integrationCdek.getTemplate(token),
        fetchLkProfile(),
      ]);

      const data = templateRes.data;

      // Шаг 2: заполняем форму данными из шаблона
      form.setFieldsValue({
        sender_company: data.sender_company,   sender_fio:   data.sender_fio,
        sender_phone:   data.sender_phone,     sender_inn:   data.sender_inn,
        sender_city:    data.sender_city_data?.full_name || '',
        recipient_fio:  data.recipient_fio,    recipient_phone: data.recipient_phone,
        recipient_city: data.recipient_city_data?.full_name || '',
        recipient_email: data.recipient_email,
        order_type: data.order_type,           im_number: data.im_number,
        seller_name: data.seller_name,         seller_ownership_form: data.seller_ownership_form,
        seller_inn: data.seller_inn,           seller_phone: data.seller_phone,
        pay_delivery_recipient: data.pay_delivery_recipient,
        pay_bank_commission:    data.pay_bank_commission,
        pickup_mode:    data.pickup_mode,      delivery_mode:  data.delivery_mode,
        pickup_address: data.pickup_address,   pickup_comment: data.pickup_comment,
        pickup_date:       data.pickup_date       ? dayjs(data.pickup_date)               : null,
        pickup_time_from:  data.pickup_time_from  ? dayjs(data.pickup_time_from,  'HH:mm') : null,
        pickup_time_to:    data.pickup_time_to    ? dayjs(data.pickup_time_to,    'HH:mm') : null,
        pickup_break_from: data.pickup_break_from ? dayjs(data.pickup_break_from, 'HH:mm') : null,
        pickup_break_to:   data.pickup_break_to   ? dayjs(data.pickup_break_to,   'HH:mm') : null,
        pickup_need_authority: data.pickup_need_authority,
        pickup_need_passport:  data.pickup_need_passport,
        delivery_address: data.delivery_address,
        delivery_comment: data.delivery_comment,
      });
      if (data.sender_city_data)    setSelectedSenderCity(data.sender_city_data);
      if (data.recipient_city_data) setSelectedRecipientCity(data.recipient_city_data);
      if (data.pickup_pvz_data)     { setSelectedPickupPvz(data.pickup_pvz_data);         form.setFieldsValue({ pickup_pvz_data: data.pickup_pvz_data }); }
      if (data.delivery_point_data) { setSelectedDeliveryPoint(data.delivery_point_data); form.setFieldsValue({ delivery_point_data: data.delivery_point_data }); }
      if (data.order_type)    setOrderType(data.order_type);
      if (data.pickup_mode)   setPickupMode(data.pickup_mode);
      if (data.delivery_mode) setDeliveryMode(data.delivery_mode);

      // Шаг 3: поля, оставшиеся пустыми, заполняем данными из ЛК
      applyLkProfileFallback(profile);

      message.success('Шаблон загружен');
    } catch (err) {
      console.error(err);
      message.error('Не удалось загрузить шаблон');
    } finally {
      setLoadingTemplate(false);
    }
  }, [token, form, fetchLkProfile, applyLkProfileFallback]);

  
  const loadTemplateRef = useRef(loadTemplate);
  useEffect(() => { loadTemplateRef.current = loadTemplate; });
  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => loadTemplateRef.current(), 200);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  
  const handleCalculateAvailable = useCallback(async () => {
    const values = form.getFieldsValue(true);
    if (!values.packages?.length)     { setAvailableTariffs([]); return; }
    if (!selectedSenderCity?.code)    { return; }
    if (!selectedRecipientCity?.code) { return; }
    setLoadingTariffs(true);
    try {
      const res = await apiRequests.integrationCdek.calculate({
        type: orderType === 'im' ? 1 : 2,
        from_location: { code: selectedSenderCity.code,    country_code: 'RU', city: values.sender_city },
        to_location:   { code: selectedRecipientCity.code, country_code: 'RU', city: values.recipient_city },
        packages: values.packages.map((p) => ({ weight: computeBoxWeight(p.items), length: BOX_LENGTH, width: BOX_WIDTH, height: BOX_HEIGHT })),
      }, token);
      const tariffs = res.data.tariff_codes || [];
      setAllTariffs(tariffs);
      filterTariffsByMode(tariffs, pickupMode, deliveryMode);
      if (tariffs.length > 0) message.success(`Найдено ${tariffs.length} тарифов`);
      else message.warning('По данному направлению тарифы не найдены');
    } catch (err) {
      console.error(err);
      message.error('Не удалось рассчитать тарифы');
    } finally {
      setLoadingTariffs(false);
    }
  }, [form, selectedSenderCity, selectedRecipientCity, orderType, token, pickupMode, deliveryMode, filterTariffsByMode]);

  
  const handleCalculatePrice = useCallback(async () => {
    const values = form.getFieldsValue(true);
    if (!selectedTariffCode || !values.packages?.length || !selectedSenderCity?.code || !selectedRecipientCity?.code) return;
    setLoadingDetailed(true);
    try {
      const res = await apiRequests.integrationCdek.calculate({
        type: orderType === 'im' ? 1 : 2, tariff_code: selectedTariffCode,
        from_location: { code: selectedSenderCity.code,    country_code: 'RU', city: values.sender_city },
        to_location:   { code: selectedRecipientCity.code, country_code: 'RU', city: values.recipient_city },
        packages: values.packages.map((p) => ({ weight: computeBoxWeight(p.items), length: BOX_LENGTH, width: BOX_WIDTH, height: BOX_HEIGHT })),
      }, token);
      setDetailedPrice(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetailed(false);
    }
  }, [form, selectedTariffCode, selectedSenderCity, selectedRecipientCity, orderType, token]);

  
  const calcAvailRef = useRef(handleCalculateAvailable);
  useEffect(() => { calcAvailRef.current = handleCalculateAvailable; });

  useEffect(() => {
    if (!selectedSenderCity || !selectedRecipientCity) return;
    const t = setTimeout(() => {
      const values = form.getFieldsValue(true);
      if (!values.packages?.length) return;
      calcAvailRef.current();
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSenderCity?.code, selectedRecipientCity?.code, items.length, orderType, pickupMode, deliveryMode]);

  
  const calcPriceRef = useRef(handleCalculatePrice);
  useEffect(() => { calcPriceRef.current = handleCalculatePrice; });

  useEffect(() => {
    if (!selectedTariffCode) return;
    const t = setTimeout(() => calcPriceRef.current(), 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTariffCode]);

  
  const loadCdekOrderByDoc = useCallback(async () => {
    if (!docId) return;
    setLoadingCreate(true);
    try {
      const res = await apiRequests.integrationCdek.getOrderByDoc(docId, token);
      const d = res.data;
      setCdekOrder({ entity: { uuid: d.order_uuid, cdek_number: d.cdek_number }, requests: [{ state: d.status }] });
    } catch (err) {
      if (err.response?.status === 404) setCdekOrder(null);
      else { console.error(err); message.error('Ошибка загрузки заказа'); }
    } finally { setLoadingCreate(false); }
  }, [docId, token]);

  
  const buildPackagesPayload = (formPackages = []) =>
    formPackages.map((pkg, idx) => ({
      number: `pkg-${idx + 1}`,
      weight: computeBoxWeight(pkg.items),
      length: BOX_LENGTH,
      width:  BOX_WIDTH,
      height: BOX_HEIGHT,
      items: (pkg.items || []).map((item) => ({
        name: item.name, ware_key: item.ware_key, weight: item.weight,
        amount: item.amount, cost: item.cost,
        payment: { value: item.cost * item.amount },
      })),
    }));

  
  const handleCreateOrder = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!selectedTariffCode) return message.error('Выберите тариф');
      setLoadingCreate(true);

      const payload = {
        type: orderType === 'im' ? 1 : 2,
        tariff_code: selectedTariffCode,
        ...(docId && { doc_sales_id: docId }),
        packages: buildPackagesPayload(values.packages),
        recipient: {
          name:   values.recipient_fio,
          phones: [{ number: values.recipient_phone }],
          ...(values.recipient_email && { email: values.recipient_email }),
        },
      };

      if (values.packages?.some((p) => p.is_dangerous)) payload.services = [{ code: 'DANGER_CARGO' }];
      if (values.pay_bank_commission) payload.comment = 'Комиссия банка с получателя';

      if (values.sender_fio && values.sender_phone) {
        payload.sender = {
          name: values.sender_fio, phones: [{ number: values.sender_phone }],
          ...(values.sender_company && { company: values.sender_company }),
          ...(values.sender_inn     && { inn:     values.sender_inn }),
        };
      }
      if (orderType === 'im' && values.seller_name) {
        payload.seller = {
          name: values.seller_name,
          ...(values.seller_inn            && { inn:            values.seller_inn }),
          ...(values.seller_phone          && { phone:          values.seller_phone }),
          ...(values.seller_ownership_form && { ownership_form: values.seller_ownership_form }),
        };
      }
      if (values.pay_delivery_recipient && calculatedPrice?.delivery_sum) {
        payload.delivery_recipient_cost = { value: calculatedPrice.delivery_sum, vat_sum: 0, vat_rate: 20 };
      }

      if (pickupMode === 'door') {
        if (!selectedSenderCity) { message.error('Не выбран город отправителя'); setLoadingCreate(false); return; }
        payload.from_location = {
          code: selectedSenderCity.code, city: selectedSenderCity.name || selectedSenderCity.full_name,
          country_code: 'RU',
          address: values.pickup_address_data?.full_address || values.pickup_address,
          ...(values.pickup_address_data?.postal_code && { postal_code: values.pickup_address_data.postal_code }),
        };
      } else if (pickupMode === 'pvz') {
        if (!values.pickup_pvz_data?.code) { message.error('Не выбран ПВЗ отправления'); setLoadingCreate(false); return; }
        payload.shipment_point = values.pickup_pvz_data.code;
      }

      if (deliveryMode === 'door') {
        if (!selectedRecipientCity) { message.error('Не выбран город получателя'); setLoadingCreate(false); return; }
        payload.to_location = {
          code: selectedRecipientCity.code, city: selectedRecipientCity.name || selectedRecipientCity.full_name,
          country_code: 'RU',
          address: values.delivery_address_data?.full_address || values.delivery_address,
          ...(values.delivery_address_data?.postal_code && { postal_code: values.delivery_address_data.postal_code }),
        };
      } else if (deliveryMode === 'pvz' || deliveryMode === 'postamat') {
        if (!values.delivery_point_data?.code) { message.error('Не выбран пункт выдачи'); setLoadingCreate(false); return; }
        payload.delivery_point = values.delivery_point_data.code;
      }

      const response = await apiRequests.integrationCdek.createOrder(payload, token);
      setCdekOrder(response.data);
      setDetailedPrice(null);
      message.success('Заказ успешно создан в СДЭК');
      if (onCdekOrderCreated) onCdekOrderCreated(response.data);
    } catch (err) {
      console.error(err);
      if (err.errorFields) message.error(`Ошибка валидации: ${err.errorFields.map((f) => f.name.join('.')).join(', ')}`);
      else if (err.response) message.error(`Ошибка API: ${err.response.status} — ${err.response.data?.message || ''}`);
      else message.error('Ошибка создания заказа в СДЭК');
    } finally { setLoadingCreate(false); }
  }, [form, selectedTariffCode, orderType, docId, calculatedPrice, pickupMode, deliveryMode, selectedSenderCity, selectedRecipientCity, token, onCdekOrderCreated]);

  
  const handleDeleteOrder = useCallback(() => {
    Modal.confirm({
      title: 'Удаление заказа', content: 'Действие необратимо.',
      okText: 'Да, удалить', okType: 'danger', cancelText: 'Отмена',
      onOk: async () => {
        setLoadingCreate(true);
        try {
          await apiRequests.integrationCdek.deleteOrder(cdekOrder.entity.uuid, token);
          message.success('Заказ успешно удалён');
          setCdekOrder(null); setDetailedPrice(null); setCalculatedPrice(null); setSelectedTariffCode(null);
          if (onCdekOrderDeleted) onCdekOrderDeleted();
        } catch (err) { console.error(err); message.error('Ошибка при удалении'); }
        finally { setLoadingCreate(false); }
      },
    });
  }, [cdekOrder, token, onCdekOrderDeleted]);

  
  const handleGetOrderInfo = useCallback(async () => {
    if (!cdekOrder?.entity?.uuid) return;
    setLoadingOrderInfo(true);
    try {
      const res = await apiRequests.integrationCdek.getOrderInfo(cdekOrder.entity.uuid, token);
      setCdekOrder((prev) => ({
        ...prev,
        entity:   { ...prev.entity, status: res.data.entity?.status, cdek_number: res.data.entity?.cdek_number },
        requests: res.data.requests || prev.requests,
      }));
      message.success('Информация обновлена');
    } catch (err) { console.error(err); message.error('Ошибка получения информации'); }
    finally { setLoadingOrderInfo(false); }
  }, [cdekOrder, token]);

  
  const printWaybill = useCallback((orderUuid) => {
    apiRequests.integrationCdek.printWaybill({ orders: [{ order_uuid: orderUuid }], copy_count: 2 }, token)
      .then((resp) => { const u = resp.data.entity?.uuid; if (u) setWaybillUuid(u); if (resp.data.entity?.url) window.open(resp.data.entity.url, '_blank'); else message.success('Квитанция сформирована'); })
      .catch(() => message.error('Ошибка печати накладной'));
  }, [token]);

  const handleGetWaybillInfo = useCallback(async () => {
    if (!waybillUuid) return message.warning('Сначала сформируйте квитанцию');
    setLoadingWaybillInfo(true);
    try { const res = await apiRequests.integrationCdek.getWaybillInfo(waybillUuid, token); setWaybillInfo(res.data); message.success('Информация получена'); }
    catch (err) { console.error(err); message.error('Ошибка'); }
    finally { setLoadingWaybillInfo(false); }
  }, [waybillUuid, token]);

  const handleDownloadWaybill = useCallback(async () => {
    if (!waybillUuid) return message.warning('Сначала сформируйте квитанцию');
    setLoadingWaybillDownload(true);
    try {
      const res = await apiRequests.integrationCdek.downloadWaybill(waybillUuid, token);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = Object.assign(document.createElement('a'), { href: url });
      link.setAttribute('download', `waybill_${waybillUuid}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Квитанция скачана');
    } catch (err) { console.error(err); message.error('Ошибка скачивания'); }
    finally { setLoadingWaybillDownload(false); }
  }, [waybillUuid, token]);

  
  const printBarcode = useCallback((orderUuid) => {
    apiRequests.integrationCdek.printBarcode({ orders: [{ order_uuid: orderUuid }], copy_count: 1, format: 'A4', lang: 'RUS' }, token)
      .then((resp) => { const u = resp.data.entity?.uuid; if (u) setBarcodeUuid(u); if (resp.data.entity?.url) window.open(resp.data.entity.url, '_blank'); else message.success('ШК сформирован'); })
      .catch(() => message.error('Ошибка печати ШК'));
  }, [token]);

  const handleGetBarcodeInfo = useCallback(async () => {
    if (!barcodeUuid) return message.warning('Сначала сформируйте ШК');
    setLoadingBarcodeInfo(true);
    try { const res = await apiRequests.integrationCdek.getBarcodeInfo(barcodeUuid, token); setBarcodeInfo(res.data); message.success('Информация получена'); }
    catch (err) { console.error(err); message.error('Ошибка'); }
    finally { setLoadingBarcodeInfo(false); }
  }, [barcodeUuid, token]);

  const handleDownloadBarcode = useCallback(async () => {
    if (!barcodeUuid) return message.warning('Сначала сформируйте ШК');
    setLoadingBarcodeDownload(true);
    try {
      const res = await apiRequests.integrationCdek.downloadBarcode(barcodeUuid, token);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = Object.assign(document.createElement('a'), { href: url });
      link.setAttribute('download', `barcode_${barcodeUuid}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      message.success('ШК скачан');
    } catch (err) { console.error(err); message.error('Ошибка скачивания'); }
    finally { setLoadingBarcodeDownload(false); }
  }, [barcodeUuid, token]);

  
  const resetCdekState = useCallback(() => {
    setAllTariffs([]); setAvailableTariffs([]); setSelectedTariffCode(null); setCalculatedPrice(null); setCdekOrder(null);
  }, []);

  return {
    orderType, setOrderType, recipientLegal, setRecipientLegal,
    pickupMode, setPickupMode, deliveryMode, setDeliveryMode,
    allTariffs, availableTariffs, selectedTariffCode, setSelectedTariffCode,
    calculatedPrice, setCalculatedPrice, detailedPrice,
    loadingTariffs, loadingCreate, loadingDetailed, loadingTemplate,
    loadingOrderInfo, loadingWaybillInfo, loadingWaybillDownload, loadingBarcodeInfo, loadingBarcodeDownload,
    cdekOrder, waybillUuid, barcodeUuid, waybillInfo, barcodeInfo,
    lkProfile,
    senderCityOptions, recipientCityOptions, loadingSenderCity, loadingRecipientCity,
    selectedSenderCity, setSelectedSenderCity, selectedRecipientCity, setSelectedRecipientCity,
    setSenderSearch, setRecipientSearch,
    pickupAddressOptions, deliveryAddressOptions, loadingPickupAddress, loadingDeliveryAddress,
    setPickupAddressSearch, setDeliveryAddressSearch,
    pickupPvzOptions, deliveryPvzOptions, loadingPickupPvz, loadingDeliveryPvz,
    selectedPickupPvz, setSelectedPickupPvz, selectedDeliveryPoint, setSelectedDeliveryPoint,
    loadCdekOrderByDoc, loadTemplate, updateFormWithItemsAndWarehouse, syncFromRegularDelivery,
    handleCalculateAvailable, handleCalculatePrice, handleCreateOrder, handleDeleteOrder,
    handleGetOrderInfo, printWaybill, printBarcode,
    handleGetWaybillInfo, handleDownloadWaybill, handleGetBarcodeInfo, handleDownloadBarcode,
    resetCdekState,
  };
};

export default useCdekOrder;